import * as React from 'react'
import { Focusable } from './Focusable'
import { classNames } from './helpers'
import type { LinkComponent } from '../types'

export interface CollectionNavItem {
  /** Identifies the item, and marks it current when it matches `activeId`. */
  id: string;
  /** What the item reads as. Translated by the caller — the styleguide ships no copy. */
  label: string;
  /** Where the item leads. */
  href: string;
}

/**
 * `tabs` is the first level: the sections of the catalogue, drawn as a tab bar with
 * a rule under it. `pills` is the second level, under a tab that has one — the same
 * list of links, but detached pills, so two stacked rows never read as two competing
 * tab bars.
 */
export type CollectionNavVariant = 'tabs' | 'pills'

export interface CollectionNavProps {
  /** The categories on offer, in the order the API returned them. */
  items: CollectionNavItem[];
  /** How the row is drawn. Defaults to `tabs` — the first-level bar. */
  variant?: CollectionNavVariant;
  /**
   * The item standing for the page you are on. The match is by `id`, not by href, so
   * a page reached with a query string (`?page=3`) still marks its own category.
   */
  activeId?: string;
  /**
   * In-app link component (e.g. React Router's `Link`). Without it the items render
   * as plain text, since the styleguide holds no router of its own.
   */
  LinkComponent?: LinkComponent;
  /**
   * Accessible name for the `<nav>` (e.g. "Categories") — the catalogue pages carry
   * a breadcrumb as well, and this is what tells the two apart in a landmark list.
   */
  label?: string;
  className?: string;
}

/**
 * The first-level navigation at the head of the catalogue pages: a tab bar with one
 * tab per category, plus whatever "everything" entry the caller puts first.
 *
 * A tab bar rather than a row of buttons because these are the sections of one
 * catalogue, not filters to combine — at most one is ever on, and the marked tab
 * says which part of the catalogue you are looking at.
 *
 * A list of links, not a set of controls: each tab is a real destination
 * (`/videos/category/:id`), so the browser's back button, a bookmark and a remote's
 * OK button all do the obvious thing. `aria-current` marks the category you are
 * looking at rather than a class alone, so the state survives for a screen reader.
 *
 * The row scrolls horizontally when the categories outrun the viewport — it is a
 * short list by nature (the API's `discover` slot returns the roots only), so it
 * stays one line rather than wrapping into a block that pushes the grid off-screen.
 */
export function CollectionNav ({ items, variant = 'tabs', activeId, LinkComponent, label, className }: CollectionNavProps) {
  const activeRef = React.useRef<HTMLLIElement>(null)

  // The row scrolls when the categories outrun the screen, and the one you are on can
  // start off-screen — most obviously on a sub-collection deep in an alphabetical
  // list. Bring it into view rather than leaving the row parked at its first item,
  // which reads as "these are all the categories there are".
  // `inline: 'nearest'` scrolls the row itself and nothing else: `block: 'nearest'`
  // keeps it from dragging the page down to the navigation on arrival.
  React.useEffect(() => {
    const element = activeRef.current
    // Guarded: this runs on TV browsers years behind (see focus.ts), and an engine
    // without the options overload would otherwise scroll the page to the item.
    if (!element?.scrollIntoView) return
    try {
      element.scrollIntoView({ block: 'nearest', inline: 'nearest' })
    } catch {
      // An engine that only takes the boolean form: leave the row where it is
      // rather than jumping the page.
    }
  }, [activeId, items])

  if (items.length === 0) return null
  return (
    <nav className={classNames('mlsg-collection-nav', `mlsg-collection-nav--${variant}`, className)} aria-label={label}>
      <ul className='mlsg-collection-nav__list'>
        {items.map(item => {
          const isActive = item.id === activeId
          return (
            // The current-item state is carried by the <li>, not by the pill inside
            // it, because the pill's text colour has to come from an ancestor: the
            // app shell sets `a { color: inherit !important }` globally, so a colour
            // declared on the link itself is thrown away and the pill would render
            // white-on-white. The <li> colours, the pill inherits.
            <li
              className={classNames('mlsg-collection-nav__item', isActive && 'mlsg-collection-nav__item--active')}
              key={item.id}
              ref={isActive ? activeRef : undefined}
            >
              {LinkComponent
                ? (
                  // Its own stop, so a remote reaches the categories on the way down
                  // to the grid instead of landing straight in the tiles.
                  <Focusable className='mlsg-focusable--inline'>
                    <LinkComponent
                      to={item.href}
                      className='mlsg-collection-nav__link'
                      aria-current={isActive ? 'page' : undefined}
                    >
                      {item.label}
                    </LinkComponent>
                  </Focusable>
                  )
                : (
                  <span className='mlsg-collection-nav__link' aria-current={isActive ? 'page' : undefined}>
                    {item.label}
                  </span>
                  )}
            </li>
          )
        })}
      </ul>
    </nav>
  )
}
